// ============================================================
// GYM — Mobile session tracker
// ============================================================
import { createClient } from '@supabase/supabase-js';
import * as _sessions from './modules/sessions.js';
import { setSupabaseRef, loadSettings } from './modules/config.js';
import { setDateFormat, formatDate, escHtml } from './modules/utils.js';

const SUPABASE_URL = 'https://vgnrfskpestbpvlygiua.supabase.co';
const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZnbnJmc2twZXN0YnB2bHlnaXVhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ5MDg5MDIsImV4cCI6MjA5MDQ4NDkwMn0.pgpi6ooHlXd8DVS0H7J9r6_CEJmwpKB_OFH2Ic1R7c4';

const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);
setSupabaseRef(supabase);

const _cfg = loadSettings();
setDateFormat(_cfg.dateFormat || 'MMM D, YYYY');

// ── State ─────────────────────────────────────────────────────
let gymClients        = [];
let currentUser       = null;
let _currentClientId  = null;
let _detailClientId   = null;
let _outcome          = null;
let _who              = null;
let _inDetailView     = false;
let _gymSearch        = '';
// ⚠️ UP HERE, NOT BESIDE scheduleCounts: the first session refresh runs inside `await boot()` below, before the
// module reaches any later `let`, and reading it there threw (swallowed by the refresh, first repaint skipped).
let _countsTimer      = null;

// ── Helpers ───────────────────────────────────────────────────
function _clientInitials(c) {
  const f = (c.firstName || '').trim(), l = (c.lastName || '').trim();
  if (f && l) return (f[0] + l[0]).toUpperCase();
  const parts = (c.name || '').trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (parts[0] || '??').slice(0, 2).toUpperCase();
}
function _avatarColor(id) {
  const COLORS = ['#7c3aed','#2563eb','#059669','#d97706','#dc2626','#0891b2','#65a30d','#c026d3','#ea580c','#0d9488'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
  return COLORS[Math.abs(h) % COLORS.length];
}
function _lastSessionStr(clientId) {
  const logs = _sessions.getSessionLogs()
    .filter(s => s.clientId === clientId && s.date)
    .sort((a, b) => b.date.localeCompare(a.date));
  return logs.length ? `Last: ${formatDate(logs[0].date)}` : 'No sessions yet';
}

// ── Assign all functions to window NOW (before any await) ─────
// Function declarations below are hoisted, so this is safe.
window.openDrawer         = openDrawer;
window.closeDrawer        = closeDrawer;
window.selectDrawerClient = selectDrawerClient;
window.openClientDetail   = openClientDetail;
window.showListView       = showListView;
window.toggleGymSection   = toggleGymSection;
window.toggleGymGroup     = toggleGymGroup;
window.onGymSearch        = onGymSearch;
window.openGymModal       = openGymModal;
window.closeGymModal      = closeGymModal;
window.setGymOutcome      = setGymOutcome;
window.setGymWho          = setGymWho;
window.gymUpdatePreview   = gymUpdatePreview;
window.saveGymSession     = saveGymSession;
window.gymRefresh         = gymRefresh;

// ── Auth ──────────────────────────────────────────────────────
const { data: { session } } = await supabase.auth.getSession();
if (!session) {
  window.location.href = `login.html?redirect=${encodeURIComponent('gym.html')}`;
} else {
  currentUser = session.user;
  await boot();
}

// ── Boot ──────────────────────────────────────────────────────
async function boot() {
  await loadClients();
  // ⚠️ ONE COUNT (one sessions system, part 7): where the switch is on, the numbers are the server's, loaded
  // before the first paint and again whenever a session changes. Off: nothing loads, the page counts as before.
  await _sessions.loadSessionCounts().catch(err => console.warn('[gym] the count did not load:', err.message));
  await _sessions.initSessionListener(() => {
    scheduleCounts();
    renderDrawerList();
    if (_inDetailView && _detailClientId) renderDetail(_detailClientId);
  });
  openDrawer();
}

async function loadClients() {
  const { data: rows, error } = await supabase.from('clients').select('id, data');
  if (error) { showToast('Failed to load clients.', 'error'); return; }
  // Every active client; the drawer shows those with sessions (_sessions.sessionsShownFor), once the credits are read
  gymClients = (rows || [])
    .map(r => ({ id: r.id, ...r.data }))
    .filter(c => c.status === 'active')
    .sort((a, b) =>
      (a.firstName || a.name || '').toLowerCase()
        .localeCompare((b.firstName || b.name || '').toLowerCase())
    );
}

/** A session changed: ask the server for the count again, once for a burst of changes, then repaint. */
function scheduleCounts() {
  clearTimeout(_countsTimer);
  _countsTimer = setTimeout(() => {
    _sessions.loadSessionCounts().then((loaded) => {
      if (!loaded) return;
      renderDrawerList();
      if (_inDetailView && _detailClientId) renderDetail(_detailClientId);
    }, err => console.warn('[gym] the count did not load:', err.message));
  }, 400);
}

// ── Drawer ────────────────────────────────────────────────────
function renderDrawerList() {
  const el = document.getElementById('gym-drawer-list');
  if (!el) return;
  // In-person and hybrid clients, and an online client who has had a session pack (10/16)
  const shown = gymClients.filter((c) => _sessions.sessionsShownFor(c));
  if (!shown.length) {
    el.innerHTML = `<div class="gym-empty" style="padding:24px 16px">No clients with sessions.</div>`;
    return;
  }

  function itemHtml(c) {
    const bal      = _sessions.sessionsFree(c.id);
    const expiring = _sessions.sessionExpiringSoon(c.id, 14);
    const balCls   = bal === 0 ? 'gym-drawer-bal--zero' : expiring > 0 ? 'gym-drawer-bal--warn' : 'gym-drawer-bal--ok';
    const initials = _clientInitials(c);
    const color    = _avatarColor(c.id);
    const lastDate = _lastSessionStr(c.id);
    return `<button class="gym-drawer-item${_detailClientId === c.id ? ' active' : ''}"
        onclick="window.selectDrawerClient('${c.id}')">
      <span class="gym-item-avatar" style="background:${color}">${escHtml(initials)}</span>
      <span class="gym-item-body">
        <span class="gym-drawer-item-name">${escHtml(clientName(c))}</span>
        <span class="gym-item-last">${lastDate}</span>
      </span>
      <span class="gym-drawer-bal ${balCls}">${bal}</span>
    </button>`;
  }

  // Flat search results when filtering
  if (_gymSearch) {
    const q = _gymSearch.toLowerCase();
    const filtered = shown.filter(c => clientName(c).toLowerCase().includes(q));
    el.innerHTML = filtered.length
      ? filtered.map(itemHtml).join('')
      : `<div class="gym-empty" style="padding:16px;font-style:italic;opacity:0.6">No clients match</div>`;
    return;
  }

  const withCredits = shown.filter(c => _sessions.sessionsFree(c.id) > 0);
  const noCredits   = shown.filter(c => _sessions.sessionsFree(c.id) === 0);
  const chevron     = `<svg class="gym-list-group-chevron" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M7 10l5 5 5-5z"/></svg>`;

  el.innerHTML = `
    <div class="gym-list-group">
      <button class="gym-list-group-header" onclick="window.toggleGymGroup(this)">
        <span class="gym-list-group-count">${withCredits.length}</span>
        <span class="gym-list-group-label">Has sessions</span>
        ${chevron}
      </button>
      <div class="gym-list-group-items">
        ${withCredits.length ? withCredits.map(itemHtml).join('') : '<div class="gym-empty" style="padding:12px 16px;font-size:14px;font-style:italic;opacity:0.6">None</div>'}
      </div>
    </div>
    <div class="gym-list-group collapsed">
      <button class="gym-list-group-header" onclick="window.toggleGymGroup(this)">
        <span class="gym-list-group-count">${noCredits.length}</span>
        <span class="gym-list-group-label">No credits</span>
        ${chevron}
      </button>
      <div class="gym-list-group-items">
        ${noCredits.length ? noCredits.map(itemHtml).join('') : '<div class="gym-empty" style="padding:12px 16px;font-size:14px;font-style:italic;opacity:0.6">None</div>'}
      </div>
    </div>`;
}

function toggleGymGroup(btn) {
  btn.closest('.gym-list-group').classList.toggle('collapsed');
}

function onGymSearch(val) {
  _gymSearch = val.trim();
  renderDrawerList();
}

function openDrawer() {
  renderDrawerList();
  document.getElementById('gym-drawer').classList.add('open');
  document.getElementById('gym-drawer-overlay').classList.remove('hidden');
  document.body.style.overflow = 'hidden';
}

function closeDrawer() {
  document.getElementById('gym-drawer').classList.remove('open');
  document.getElementById('gym-drawer-overlay').classList.add('hidden');
  document.body.style.overflow = '';
}

function selectDrawerClient(clientId) {
  closeDrawer();
  openClientDetail(clientId);
}

// ── Client detail view ────────────────────────────────────────
function openClientDetail(clientId) {
  _detailClientId = clientId;
  _inDetailView   = true;
  renderDetail(clientId);
  document.getElementById('gym-view-list').classList.add('hidden');
  document.getElementById('gym-view-detail').classList.remove('hidden');
  const c = gymClients.find(x => x.id === clientId);
  document.getElementById('gym-header-title').textContent = c ? clientName(c) : 'Client';
  document.getElementById('gym-menu-btn').classList.add('hidden');
  document.getElementById('gym-back-btn').classList.remove('hidden');
  window.scrollTo(0, 0);
}

function showListView() {
  _inDetailView   = false;
  _detailClientId = null;
  document.getElementById('gym-view-detail').classList.add('hidden');
  document.getElementById('gym-view-list').classList.remove('hidden');
  document.getElementById('gym-header-title').textContent = 'Sessions';
  document.getElementById('gym-back-btn').classList.add('hidden');
  document.getElementById('gym-menu-btn').classList.remove('hidden');
  window.scrollTo(0, 0);
}

function renderDetail(clientId) {
  const el = document.getElementById('gym-detail');
  if (!el) return;
  const c = gymClients.find(x => x.id === clientId);
  if (!c) { el.innerHTML = `<div class="gym-empty">Client not found.</div>`; return; }

  const bal        = _sessions.sessionsFree(clientId);
  const counts     = _sessions.sessionCounts(clientId);
  const reschedits = _sessions.getSessionReschedits().filter(r => r.clientId === clientId);
  const credits    = _sessions.getSessionCredits().filter(p => p.clientId === clientId);
  const logs       = _sessions.getSessionLogs()
    .filter(l => l.clientId === clientId)
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const availResched = reschedits.filter(r => r.status === 'available' && !isExpiredStr(r.expiryDate)).length;
  const availIssued  = _sessions.availableSessionCredits(clientId).reduce((s, p) => s + p.remaining, 0);
  const balCls       = bal === 0 ? 'gym-bal--zero' : 'gym-bal--ok';

  // The Session Tracker's three numbers where the server's count is loaded (the switch is on).
  let html = counts ? `
    <div class="gym-detail-summary">
      <div class="gym-detail-tile">
        <span class="gym-detail-tile-num ${balCls}">${counts.free}</span>
        <span class="gym-detail-tile-label">Available</span>
      </div>
      <div class="gym-detail-tile">
        <span class="gym-detail-tile-num">${counts.setAside ?? 0}</span>
        <span class="gym-detail-tile-label">Booked</span>
      </div>
      <div class="gym-detail-tile">
        <span class="gym-detail-tile-num">${counts.used ?? 0}</span>
        <span class="gym-detail-tile-label">Used</span>
      </div>
    </div>` : `
    <div class="gym-detail-summary">
      <div class="gym-detail-tile">
        <span class="gym-detail-tile-num ${balCls}">${bal}</span>
        <span class="gym-detail-tile-label">Total Credits</span>
      </div>
      <div class="gym-detail-tile">
        <span class="gym-detail-tile-num">${availIssued}</span>
        <span class="gym-detail-tile-label">Issued</span>
      </div>
      <div class="gym-detail-tile">
        <span class="gym-detail-tile-num">${availResched}</span>
        <span class="gym-detail-tile-label">Makeup</span>
      </div>
    </div>`;
  html += `
    <button class="gym-detail-log-btn" onclick="window.openGymModal('${clientId}')"
      ${bal === 0 ? 'disabled' : ''}>
      ${bal === 0 ? '❌ No credits available' : '+ Log session'}
    </button>`;

  // Rescheduled credits
  const reschedSorted = [...reschedits].sort((a, b) => (a.expiryDate || '').localeCompare(b.expiryDate || ''));
  html += `<div class="gym-section">
    <div class="gym-section-header" onclick="window.toggleGymSection('gym-sec-resched')">
      <span>Makeup Credits</span>
      <span class="gym-section-badge">${availResched} available</span>
      <span class="gym-section-chev" id="gym-chev-resched">▼</span>
    </div>
    <div class="gym-section-body" id="gym-sec-resched">`;
  if (!reschedSorted.length) {
    html += `<div class="gym-sec-empty">No makeup credits.</div>`;
  } else {
    reschedSorted.forEach(r => {
      // ⚠️ 'consumed' IS THE WORD THE SERVER WRITES — never 'used'.
      // session_reschedits carries exactly two statuses, both from
      // scheduling-helpers.js: 'available' and 'consumed'. Testing 'used'
      // made this ALWAYS false, so a SPENT make-up credit rendered live:
      // a "{N}d left" badge under a header correctly saying "0 available",
      // and inside 14 days it went yellow-urgent — the card shouting about
      // a credit nobody can spend (2026-08-26).
      // ⚠️ THIS LOGIC EXISTS IN THREE PLACES (app.js, gym.js,
      // modules/sessions.js). Change one, change all three.
      const used    = r.status === 'consumed';
      const expired = !used && isExpiredStr(r.expiryDate);
      const dimmed  = used || expired;
      const days    = daysUntil(r.expiryDate);
      const badge   = used ? '✓ Used' : expired ? '✗ Expired' : `${days}d left`;
      const badgeCls = used ? 'gym-badge-muted' : expired ? 'gym-badge-red' : days <= 14 ? 'gym-badge-yellow' : 'gym-badge-purple';
      html += `<div class="gym-sec-row${dimmed ? ' dimmed' : ''}">
        <div class="gym-sec-row-body">
          <div class="gym-sec-row-title">From ${formatDate(r.originalSessionDate)}</div>
          <div class="gym-sec-row-sub">Expires ${formatDate(r.expiryDate)}${r.cancelledBy ? ' · ' + (r.cancelledBy === 'client' ? 'Client cancelled' : 'Coach cancelled') : ''}</div>
        </div>
        <span class="gym-badge ${badgeCls}">${badge}</span>
      </div>`;
    });
  }
  html += `</div></div>`;

  // Issued credits
  const creditsSorted = [...credits].sort((a, b) => (b.issuedDate || '').localeCompare(a.issuedDate || ''));
  const totalIssued   = creditsSorted.reduce((s, p) => s + (p.remaining || 0), 0);
  html += `<div class="gym-section">
    <div class="gym-section-header" onclick="window.toggleGymSection('gym-sec-issued')">
      <span>Issued Credits</span>
      <span class="gym-section-badge">${totalIssued} remaining</span>
      <span class="gym-section-chev" id="gym-chev-issued">▼</span>
    </div>
    <div class="gym-section-body" id="gym-sec-issued">`;
  if (!creditsSorted.length) {
    html += `<div class="gym-sec-empty">No issued credits.</div>`;
  } else {
    creditsSorted.forEach(p => {
      const exhausted = p.remaining === 0;
      const expired   = !exhausted && isExpiredStr(p.expiryDate);
      const dimmed    = exhausted || expired;
      const pct       = p.qty > 0 ? Math.round((p.remaining / p.qty) * 100) : 0;
      const label     = p.periodLabel || p.periodDisplay || formatDate(p.issuedDate);
      const badge     = exhausted ? 'Exhausted' : expired ? 'Expired' : `${p.remaining} left`;
      const badgeCls  = (exhausted || expired) ? 'gym-badge-muted' : pct > 50 ? 'gym-badge-blue' : pct > 20 ? 'gym-badge-yellow' : 'gym-badge-red';
      html += `<div class="gym-sec-row${dimmed ? ' dimmed' : ''}">
        <div class="gym-sec-row-body">
          <div class="gym-sec-row-title">${esc(label)}</div>
          ${p.expiryDate ? `<div class="gym-sec-row-sub">Expires ${formatDate(p.expiryDate)}</div>` : ''}
        </div>
        <span class="gym-badge ${badgeCls}">${badge}</span>
      </div>`;
    });
  }
  html += `</div></div>`;

  // Session history
  const recent = logs.slice(0, 20);
  html += `<div class="gym-section">
    <div class="gym-section-header" onclick="window.toggleGymSection('gym-sec-history')">
      <span>Session History</span>
      <span class="gym-section-badge">${logs.length} total</span>
      <span class="gym-section-chev" id="gym-chev-history">▼</span>
    </div>
    <div class="gym-section-body" id="gym-sec-history">`;
  if (!recent.length) {
    html += `<div class="gym-sec-empty">No sessions logged yet.</div>`;
  } else {
    recent.forEach(l => {
      const icon  = l.outcome === 'completed' ? '✅' : l.outcome === 'resched' ? '🔄' : '❌';
      const label = l.outcome === 'completed' ? 'Completed' : l.outcome === 'resched' ? 'Rescheduled' : 'Forfeit';
      const days  = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      const d     = new Date((l.date || '') + 'T00:00:00');
      const dayLabel = l.date ? `${days[d.getDay()]} ${formatDate(l.date)}` : '—';
      html += `<div class="gym-sec-row">
        <div class="gym-sec-row-icon">${icon}</div>
        <div class="gym-sec-row-body">
          <div class="gym-sec-row-title">${dayLabel}</div>
          ${l.notes ? `<div class="gym-sec-row-sub">${esc(l.notes)}</div>` : ''}
        </div>
        <span class="gym-badge gym-badge-muted">${label}</span>
      </div>`;
    });
    if (logs.length > 20) {
      html += `<div class="gym-sec-empty">${logs.length - 20} older sessions not shown</div>`;
    }
  }
  html += `</div></div>`;

  el.innerHTML = html;
}

function toggleGymSection(id) {
  const body = document.getElementById(id);
  const key  = id.replace('gym-sec-', '');
  const chev = document.getElementById(`gym-chev-${key}`);
  if (!body) return;
  const collapsed = body.classList.toggle('collapsed');
  if (chev) chev.textContent = collapsed ? '▶' : '▼';
}

// ── Log Session Modal ─────────────────────────────────────────
function openGymModal(clientId) {
  _currentClientId = clientId;
  _outcome = null;
  _who     = null;
  const c  = gymClients.find(x => x.id === clientId);
  document.getElementById('gym-modal-client-name').textContent =
    c ? (c.firstName && c.lastName ? `${c.firstName} ${c.lastName}` : c.name || 'Client') : 'Client';
  document.getElementById('gym-log-date').value = new Date().toISOString().slice(0, 10);
  ['completed','resched','forfeit'].forEach(o =>
    document.getElementById(`gym-btn-${o}`)?.classList.remove(`sel-${o}`)
  );
  document.getElementById('gym-who-wrap').classList.add('hidden');
  document.getElementById('gym-who-client').classList.remove('sel');
  document.getElementById('gym-who-coach').classList.remove('sel');
  const prev = document.getElementById('gym-preview');
  prev.className = 'gym-preview';
  prev.innerHTML = '';
  document.getElementById('gym-log-modal').classList.remove('hidden');
}

function closeGymModal() {
  document.getElementById('gym-log-modal').classList.add('hidden');
}

function setGymOutcome(o) {
  _outcome = o;
  ['completed','resched','forfeit'].forEach(x =>
    document.getElementById(`gym-btn-${x}`)?.classList.remove(`sel-${x}`)
  );
  document.getElementById(`gym-btn-${o}`)?.classList.add(`sel-${o}`);
  document.getElementById('gym-who-wrap').classList.toggle('hidden', o !== 'resched');
  if (o !== 'resched') {
    _who = null;
    document.getElementById('gym-who-client').classList.remove('sel');
    document.getElementById('gym-who-coach').classList.remove('sel');
  }
  gymUpdatePreview();
}

function setGymWho(who) {
  _who = who;
  document.getElementById('gym-who-client').classList.toggle('sel', who === 'client');
  document.getElementById('gym-who-coach').classList.toggle('sel', who === 'coach');
  gymUpdatePreview();
}

function gymUpdatePreview() {
  const previewEl = document.getElementById('gym-preview');
  if (!previewEl || !_currentClientId || !_outcome) {
    if (previewEl) { previewEl.className = 'gym-preview'; previewEl.innerHTML = ''; }
    return;
  }
  const bal      = _sessions.sessionsFree(_currentClientId);
  const date     = document.getElementById('gym-log-date')?.value;
  const settings = loadSettings();
  let msg = '', cls = 'gym-preview-yellow';

  if (_outcome === 'completed' || _outcome === 'forfeit') {
    if (bal > 0) {
      msg = _outcome === 'completed'
        ? `✅ Uses 1 credit · ${bal - 1} remaining after`
        : `❌ Forfeits 1 credit · ${bal - 1} remaining`;
      cls = _outcome === 'completed' ? 'gym-preview-green' : 'gym-preview-red';
    } else {
      msg = '❌ No credits available'; cls = 'gym-preview-red';
    }
  } else if (_outcome === 'resched') {
    // A reschedule touches no credit (2026-08-26) — the session simply moved.
    msg = '🔄 No credit is used · The session simply moved';
  }
  previewEl.className = `gym-preview ${cls}`;
  previewEl.innerHTML = msg;
}

async function saveGymSession() {
  const date = document.getElementById('gym-log-date')?.value;
  if (!date)     { showToast('Date is required.', 'error'); return; }
  if (!_outcome) { showToast('Select an outcome.', 'error'); return; }
  if (_outcome === 'resched' && !_who) { showToast('Select who cancelled.', 'error'); return; }
  const btn = document.getElementById('gym-save-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    await _sessions.logSessionEntry(_currentClientId, currentUser?.id, date, _outcome, '', _who || null);
    scheduleCounts();
    closeGymModal();
    showToast('Session logged!', 'success');
  } catch (err) {
    showToast(err.message || 'Failed to log session.', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Log session'; }
  }
}

async function gymRefresh() {
  const btn = document.getElementById('gym-refresh');
  if (btn) btn.style.opacity = '0.4';
  await loadClients();
  await _sessions.loadSessionCounts().catch(err => console.warn('[gym] the count did not load:', err.message));
  renderDrawerList();
  if (_inDetailView && _detailClientId) renderDetail(_detailClientId);
  if (btn) btn.style.opacity = '';
}

// ── Helpers ───────────────────────────────────────────────────
function clientName(c) {
  return esc(c.firstName && c.lastName ? `${c.firstName} ${c.lastName}` : c.name || 'Unknown');
}

function esc(s) {
  return String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function isExpiredStr(s) {
  return s ? s < new Date().toISOString().slice(0, 10) : false;
}

function daysUntil(s) {
  if (!s) return Infinity;
  return Math.ceil((new Date(s + 'T00:00:00') - new Date(new Date().toISOString().slice(0,10) + 'T00:00:00')) / 86400000);
}

function showToast(msg, type = '') {
  const el = document.getElementById('gym-toast');
  if (!el) return;
  el.textContent = msg;
  el.className = `gym-toast${type ? ' ' + type : ''} show`;
  clearTimeout(el._t);
  el._t = setTimeout(() => el.classList.remove('show'), 2800);
}
